import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type {
  MarChartOutcomeRecordDetails,
  MarChartPopoverRecord
} from '../../../types'

type AdministeredContentProps = {
  record: MarChartPopoverRecord
  recordDetails?: MarChartOutcomeRecordDetails
  type: 'scheduled' | 'prn'
  residentFirstName: string
}

const AdministeredContent = ({
  record,
  recordDetails,
  type,
  residentFirstName
}: AdministeredContentProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  if (!recordDetails) {
    return null
  }

  const medicationName = record.medication?.name || ''
  const dose = record.dose || ''
  const hour = record.hour || ''
  const parseDose = (
    str: string
  ): { number: string; unit?: string } | { freeText: string } | null => {
    if (!str) return null
    const match = str.match(/^([\d.]+)\s+(.+)$/)
    if (match) return { number: match[1], unit: match[2] }
    if (/^[\d.]+$/.test(str)) return { number: str }
    return { freeText: str }
  }

  const doseParsed = parseDose(dose)
  const quantityParsed = parseDose(String(recordDetails.quantity || ''))

  const doseUnit =
    doseParsed && !('freeText' in doseParsed)
      ? (doseParsed.unit ?? 'tablet')
      : 'tablet'

  let quantity: string
  let unit: string
  if (!quantityParsed) {
    if (!doseParsed) {
      quantity = ''
      unit = 'tablet'
    } else if ('freeText' in doseParsed) {
      quantity = doseParsed.freeText
      unit = ''
    } else {
      quantity = doseParsed.number
      unit = doseParsed.unit ?? 'tablet'
    }
  } else if ('freeText' in quantityParsed) {
    quantity = quantityParsed.freeText
    unit = ''
  } else if (quantityParsed.unit) {
    quantity = quantityParsed.number
    unit = quantityParsed.unit
  } else {
    quantity = quantityParsed.number
    unit = doseUnit
  }

  const doseDisplay = unit ? `${quantity} ${unit}` : quantity
  const description =
    type === 'scheduled'
      ? `For ${residentFirstName}'s ${hour} scheduled meds, ${doseDisplay} ${medicationName} was administered.`
      : `For ${residentFirstName}'s PRN meds, ${doseDisplay} ${medicationName} was administered.`

  return (
    <>
      <Label level={2}>{t('DETAILS')}</Label>
      <Value>{description}</Value>
    </>
  )
}

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 400;
`

export default AdministeredContent
